import { ProductType } from '@/AppGlobal';
import { ReactComponent as CloseSvg } from '@/images/cross.svg';
import { nativeApi } from '@/../../common/src/utils/nativeApi';
import { Platform } from '@/../../common/src/utils/nativeApi/interface';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import capiicon from './capiicon.png';
import style from './adBanner.module.less';

// byClaude-start 2026-08-14 14:27:46 落地页顶部广告位：图片 + 标题 + 下载按钮 + 关闭按钮

// byClaude-start 2026-08-26 17:15:00 单例标记：确保全局最多只有一个 AdBanner 实例在渲染
let isAdBannerMounted = false;
// byClaude-end 2026-08-26 17:15:00

type AdBannerProps = {
  productType?: ProductType;
};

// byClaude-start 2026-08-19 14:19:00 下载按钮按环境（sit/uat/prod）+ 产品（CL/KKC）跳转对应下载链接
// prod 链接已由用户提供
const DOWNLOAD_URL_MAP: Partial<Record<ProductType, Record<'sit' | 'uat' | 'prod', string>>> = {
  [ProductType.KKC]: {
    sit: 'https://beijingquycn914.onelink.me/tysz/fkwvg2fh',
    uat: 'https://beijingquycn914.onelink.me/tysz/cg6k8hy8',
    prod: 'https://beijingquycn914.onelink.me/tysz/eysddnsa',
  },
  [ProductType.CL]: {
    sit: 'https://beijingquycn914.onelink.me/tysz/1grom9dq',
    uat: 'https://beijingquycn914.onelink.me/tysz/n8a2eevr',
    prod: 'https://beijingquycn914.onelink.me/tysz/t29qlavf',
  },
};

// 根据当前 URL 域名区分环境：含 sit/test → sit，含 uat → uat，其余 → prod
const resolveEnv = (): 'sit' | 'uat' | 'prod' => {
  const host = window.location.hostname.toLowerCase();
  if (host.includes('sit') || host.includes('test')) {
    return 'sit';
  }
  if (host.includes('uat')) {
    return 'uat';
  }
  return 'prod';
};
// byClaude-end 2026-08-19 14:19:00

// byClaude-start 2026-08-24 16:04:35 sessionStorage keys for banner state management
const SESSION_KEY_BANNER_CLOSED = 'adBanner_closed';
const SESSION_KEY_BANNER_ACTIVATED = 'adBanner_activated';
const SESSION_KEY_BANNER_PRODUCT_TYPE = 'adBanner_productType';
// byClaude-end 2026-08-24 16:04:35

// byClaude-start 2026-08-24 16:40:00 判断当前页面是否是刷新操作
const isPageReload = (): boolean => {
  const navEntry = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return navEntry?.type === 'reload';
};
// byClaude-end 2026-08-24 16:40:00

// byClaude-start 2026-08-24 16:04:35 激活 banner（在注册登录页调用）
// byClaude-start 2026-08-26 17:06:52 修复：激活时清除关闭标记，确保杀掉 app 重新进入后 banner 能正常显示
// byClaude-start 2026-08-26 17:12:53 修复：触发自定义事件通知 AdBanner 组件重新检查状态，解决执行顺序问题
const AD_BANNER_ACTIVATE_EVENT = 'adBanner:activate';

export const activateAdBanner = (productType: ProductType) => {
  sessionStorage.setItem(SESSION_KEY_BANNER_ACTIVATED, 'true');
  sessionStorage.setItem(SESSION_KEY_BANNER_PRODUCT_TYPE, productType);
  sessionStorage.removeItem(SESSION_KEY_BANNER_CLOSED);
  // 触发自定义事件，通知 AdBanner 组件状态已更新
  window.dispatchEvent(new CustomEvent(AD_BANNER_ACTIVATE_EVENT));
};
// byClaude-end 2026-08-26 17:12:53
// byClaude-end 2026-08-26 17:06:52
// byClaude-end 2026-08-24 16:04:35

function AdBanner({ productType: propProductType }: AdBannerProps) {
  // byClaude-start 2026-08-26 17:15:00 单例检查：如果已有实例在渲染，当前实例不渲染
  const [isSingleton] = useState(() => {
    if (isAdBannerMounted) {
      return false; // 已有实例，当前实例不渲染
    }
    isAdBannerMounted = true;
    return true;
  });

  useEffect(
    () => () => {
      if (isSingleton) {
        isAdBannerMounted = false;
      }
    },
    [isSingleton]
  );
  // byClaude-end 2026-08-26 17:15:00

  // byClaude-start 2026-08-24 16:04:35 使用 sessionStorage 存储关闭状态，整个会话中持续显示
  const isMobileWeb = nativeApi.name === Platform.MobileWebClient;
  // byClaude-start 2026-08-27 16:30:00 判断是否在 iframe 中，iframe 内不显示 AdBanner（防止第三方回调时重复显示）
  const isInIframe = window.self !== window.top;
  // byClaude-end 2026-08-27 16:30:00
  const [visible, setVisible] = useState(() => {
    if (!isSingleton || !isMobileWeb) return false;
    // byClaude-start 2026-08-24 16:40:00 刷新页面时清除关闭标记，恢复显示 banner
    if (isPageReload()) {
      sessionStorage.removeItem(SESSION_KEY_BANNER_CLOSED);
    }
    // byClaude-end 2026-08-24 16:40:00
    const isClosed = sessionStorage.getItem(SESSION_KEY_BANNER_CLOSED) === 'true';
    const isActivated = sessionStorage.getItem(SESSION_KEY_BANNER_ACTIVATED) === 'true';
    return isActivated && !isClosed;
  });

  // 从 props 或 sessionStorage 中获取 productType
  const productType =
    propProductType || (sessionStorage.getItem(SESSION_KEY_BANNER_PRODUCT_TYPE) as ProductType);
  // byClaude-end 2026-08-24 16:04:35

  // byClaude-start 2026-08-24 16:04:35 监听 sessionStorage 变化，同步状态
  useEffect(() => {
    if (!isMobileWeb) return;
    const handleStorageChange = () => {
      const isClosed = sessionStorage.getItem(SESSION_KEY_BANNER_CLOSED) === 'true';
      const isActivated = sessionStorage.getItem(SESSION_KEY_BANNER_ACTIVATED) === 'true';
      setVisible(isActivated && !isClosed);
    };

    // byClaude-start 2026-08-26 17:12:53 监听 activateAdBanner 触发的自定义事件，解决执行顺序问题
    const handleActivate = () => {
      const isClosed = sessionStorage.getItem(SESSION_KEY_BANNER_CLOSED) === 'true';
      const isActivated = sessionStorage.getItem(SESSION_KEY_BANNER_ACTIVATED) === 'true';
      setVisible(isActivated && !isClosed);
    };
    // byClaude-end 2026-08-26 17:12:53

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(AD_BANNER_ACTIVATE_EVENT, handleActivate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(AD_BANNER_ACTIVATE_EVENT, handleActivate);
    };
  }, [isMobileWeb]);
  // byClaude-end 2026-08-24 16:04:35

  // byClaude-start 2026-08-26 16:20:00 AdBanner 渲染完成后自动滚动到页面顶部，确保用户能看到广告位
  useEffect(() => {
    if (visible) {
      // 使用 requestAnimationFrame 确保在 DOM 更新后再滚动
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }, [visible]);
  // byClaude-end 2026-08-26 16:20:00

  // byClaude-start 2026-08-19 14:19:00 点击下载按钮跳转到对应环境+产品的下载链接
  const handleDownload = () => {
    if (!productType) return;
    const url = DOWNLOAD_URL_MAP[productType]?.[resolveEnv()];
    if (url) {
      window.location.href = url;
    }
  };
  // byClaude-end 2026-08-19 14:19:00

  // byClaude-start 2026-08-24 16:04:35 关闭 banner 时设置 sessionStorage 标记
  const handleClose = () => {
    sessionStorage.setItem(SESSION_KEY_BANNER_CLOSED, 'true');
    setVisible(false);
  };
  // byClaude-end 2026-08-24 16:04:35

  // byClaude-start 2026-08-27 16:40:00 iframe 内不显示 AdBanner
  if (!visible || isInIframe) {
    return null;
  }
  // byClaude-end 2026-08-27 16:40:00

  return (
    // byClaude-start 2026-08-26 10:30:00 使用 relative 定位，广告位独立占据高度不遮挡页面内容
    <div className={style.adBanner}>
      <div className={style.adBannerContent}>
        <img className={style.adBannerIcon} src={capiicon} alt="" />
        <span className={style.adBannerTitle}>{t('pub.adBanner.title')}</span>
        <div className={style.adBannerDownload} onClick={handleDownload}>
          {t('pub.adBanner.download')}
        </div>
        <CloseSvg className={style.adBannerClose} onClick={handleClose} />
      </div>
    </div>
    // byClaude-end 2026-08-26 10:30:00
  );
}
// byClaude-end 2026-08-14 14:27:46

export default AdBanner;

// Contains claude-generated edits.
